import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Loader from '../../../common/Loader';
import BookingModal from '../../../book/BookingModal';
import styles from './SingleProvider.module.css';
import { getPublicOfferDetails, getPublicServices } from '../../../../api/user/serviceProvider/service-provider';
import ServicesComponent from './ServicesComponent';
import UserProtectedRoute from '../../../protected-routes/user-protected-route';

const SingleProvider = () => {
  const { t } = useTranslation();
  const { offerId } = useParams();
  const [booking, setBooking] = useState(false);
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [relatedOffers, setRelatedOffers] = useState([]);
  const [loadingRelated, setLoadingRelated] = useState(false);
  const [isVerifyingSession, setIsVerifyingSession] = useState(false);

  useEffect(() => {
    fetchOfferDetails();
  }, [offerId]);

  const fetchOfferDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await getPublicOfferDetails(offerId);
      
      if (result.success) {
        const offerData = result.data || result.offer;
        setOffer(offerData);
        
        if (offerData.service?._id || offerData.service) {
          fetchRelatedOffers(offerData.service?._id || offerData.service);
        }
      } else {
        setError(result.error || t('single_provider.error_title'));
      }
    } catch (error) {
      console.error('Failed to load offer details:', error);
      setError(t('single_provider.error_title'));
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps

  const fetchRelatedOffers = async (serviceId) => {
    try {
      setLoadingRelated(true);
      const result = await getPublicServices();
      
      if (result.success) {
        const offers = result.data || result.services || [];
        
        const related = offers.filter(relatedOffer => 
          (relatedOffer.service?._id === serviceId || relatedOffer.service === serviceId) &&
          relatedOffer._id !== offerId
        ).slice(0, 4);
        
        setRelatedOffers(related);
      }
    } catch (error) {
      console.error('Failed to load related offers:', error);
    } finally {
      setLoadingRelated(false);
    }
  };

  function showBookModal() {
    setBooking(!booking);
  }

  const nextImage = () => {
    if (offer.images && offer.images.length > 0) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === offer.images.length - 1 ? 0 : prevIndex + 1
      );
    }
  };

  const prevImage = () => {
    if (offer.images && offer.images.length > 0) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === 0 ? offer.images.length - 1 : prevIndex - 1
      );
    }
  };

  const getImageUrl = (imageData) => {
    // if (!imageData) return '/default-service.jpg';

    if(imageData.path){
      return imageData.path;
    }
    
  };

  // Format availability slots (e.g. [{days:['Mon'], startTime:'8am', endTime:'5pm'}]) into a readable string
  const formatAvailability = () => {
    const slots = offer.availability;
    if (Array.isArray(slots) && slots.length > 0) {
      return slots.map((slot) => {
        const days = Array.isArray(slot.days) ? slot.days.join(', ') : '';
        const time = slot.startTime && slot.endTime ? `${slot.startTime} - ${slot.endTime}` : '';
        return [days, time].filter(Boolean).join(' · ');
      }).join(' | ');
    }
    return t('single_provider.open_hours_fallback');
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <Loader label={t('single_provider.loading_offer')} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <i className="fa fa-exclamation-triangle" aria-hidden="true"></i>
          <h2>{t('single_provider.error_title')}</h2>
          <p>{error}</p>
          <button onClick={fetchOfferDetails} className={styles.tryAgainBtn}>
            <i className="fa fa-redo" aria-hidden="true"></i>
            {t('single_provider.try_again')}
          </button>
        </div>
      </div>
    );
  }

  if (!offer) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <i className="fa fa-search" aria-hidden="true"></i>
          <h2>{t('single_provider.not_found_title')}</h2>
          <p>{t('single_provider.not_found_desc')}</p>
        </div>
      </div>
    );
  }

  const isProviderActive = offer.userService?.isActive ?? false;
  const offerLocation = offer.location || offer.userService?.location || t('single_provider.location_not_specified');
  const offerExperience = offer.experience || offer.userService?.experience || 1;
  const imageCount = offer.images?.length || 0;

  const formatPriceUnit = (unit) => {
    if (!unit) return t('single_provider.per_day', '/ day');
    switch (unit) {
      case 'hour':
      case 'per_hour': return t('single_provider.per_hour', '/ hr');
      case 'service':
      case 'per_service': return t('single_provider.per_service', '/ service');
      case 'fixed': return t('single_provider.fixed', 'fixed price');
      case 'month':
      case 'per_month': return t('single_provider.per_month', '/ month');
      case 'year':
      case 'per_year': return t('single_provider.per_year', '/ year');
      case 'item':
      case 'per_item': return t('single_provider.per_item', '/ item');
      case 'day':
      case 'per_day': return t('single_provider.per_day', '/ day');
      default: return unit.startsWith('/') ? unit : `/ ${unit}`;
    }
  };

  return (
    <>
      {booking && 
        <UserProtectedRoute onValidationChange={setIsVerifyingSession} hideLoader={true}>
          <BookingModal showBookModal={showBookModal} offer={offer} />
        </UserProtectedRoute>
      }

      <div className={styles.container}>
        {/* Breadcrumb */}
        <nav className={styles.breadcrumb} aria-label="Breadcrumb">
          <Link to="/" className={styles.breadcrumbLink}>
            <i className="fa fa-home" aria-hidden="true" /> {t('single_provider.breadcrumb_home')}
          </Link>
          <span className={styles.breadcrumbSep}>›</span>
          <Link to="/serviceProvider" className={styles.breadcrumbLink}>
            {t('single_provider.breadcrumb_services')}
          </Link>
          <span className={styles.breadcrumbSep}>›</span>
          <span className={styles.breadcrumbCurrent}>{offer.userService?.name || offer.title}</span>
        </nav>

        {/* Header */}
        <div className={styles.serviceHeader}>
          <div className={styles.titleRow}>
            <div className={styles.titleWrap}>
              <h1 className={styles.serviceTitle}>{offer.userService?.name || offer.title}</h1>
              <div className={styles.headerMeta}>
                <span className={styles.metaLocation}>
                  <i className="fa fa-map-marker" aria-hidden="true"></i>
                  {offerLocation}
                </span>
                {offer.service?.name && (
                  <span className={styles.metaCategory}>
                    <i className="fa fa-tag" aria-hidden="true"></i>
                    {offer.service.name}
                  </span>
                )}
              </div>
            </div>
            <span
              className={`${styles.statusBadge} ${isProviderActive ? styles.statusActiveBadge : styles.statusInactiveBadge}`}
            >
              <span className={styles.statusBadgeDot} />
              {isProviderActive ? t('single_provider.active') : t('single_provider.inactive')}
            </span>
          </div>

          <div className={styles.priceRow}>
            <span className={styles.priceAmount}>XAF {offer.price?.toLocaleString()}</span>
            <span className={styles.pricePer}>{formatPriceUnit(offer.priceUnit)}</span>
          </div>
        </div>

        <div className={styles.mainContent}>
          <div className={styles.leftColumn}>
            <div className={styles.imageSection}>
              {imageCount > 0 ? (
                <div className={styles.sliderContainer}>
                  <img
                    src={getImageUrl(offer.images[currentImageIndex])}
                    alt={`${offer.title} - ${t('single_provider.image_counter', { current: currentImageIndex + 1, total: imageCount })}`}
                    className={styles.mainImage}
                    // onError={(e) => {
                    //   e.target.src = '/default-service.jpg';
                    // }}
                    crossOrigin="anonymous"
                  />

                  {imageCount > 1 && (
                    <>
                      <button className={styles.sliderBtnPrev} onClick={prevImage} aria-label="Previous image">
                        <i className="fa fa-chevron-left" aria-hidden="true"></i>
                      </button>
                      <button className={styles.sliderBtnNext} onClick={nextImage} aria-label="Next image">
                        <i className="fa fa-chevron-right" aria-hidden="true"></i>
                      </button>

                      <div className={styles.imageCounter}>
                        {t('single_provider.image_counter', { current: currentImageIndex + 1, total: imageCount })}
                      </div>

                      <div className={styles.sliderDots}>
                        {offer.images.map((_, index) => (
                          <button
                            key={index}
                            className={`${styles.dot} ${index === currentImageIndex ? styles.activeDot : ''}`}
                            onClick={() => setCurrentImageIndex(index)}
                            aria-label={`Image ${index + 1}`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className={styles.noImage}>
                  <i className="fa fa-image" aria-hidden="true"></i>
                  <p>{t('single_provider.no_images')}</p>
                </div>
              )}
            </div>

            <div className={styles.descriptionSection}>
              <h3 className={styles.sectionHeading}>
                <span className={styles.sectionHeadingIcon}><i className="fa fa-info-circle" aria-hidden="true"></i></span>
                {t('single_provider.about_service')}
              </h3>
              <p className={styles.serviceDescription}>
                {offer.description || t('single_provider.description_fallback')}
              </p>
            </div>
          </div>

          <div className={styles.detailsSection}>
            {/* Details cards */}
            <div className={styles.serviceDetails}>
              <div className={styles.detailItem}>
                <span className={styles.detailIcon}><i className="fa fa-clock" aria-hidden="true"></i></span>
                <div className={styles.detailContent}>
                  <h4>{t('single_provider.open_hours')}</h4>
                  <p>{formatAvailability()}</p>
                </div>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailIcon}><i className="fa fa-star" aria-hidden="true"></i></span>
                <div className={styles.detailContent}>
                  <h4>{t('single_provider.client_reviews')}</h4>
                  <p>{t('single_provider.no_rating_yet')}</p>
                </div>
              </div>

              <div className={styles.additionalInfo}>
                <h3 className={styles.sectionHeading}>
                  <span className={styles.sectionHeadingIcon}><i className="fa fa-clipboard-list" aria-hidden="true"></i></span>
                  {t('single_provider.service_information')}
                </h3>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>
                    <i className="fa fa-tag" aria-hidden="true"></i>
                    {t('single_provider.service_type')}
                  </span>
                  <span className={styles.infoValue}>{offer.service?.name || '—'}</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>
                    <i className="fa fa-briefcase" aria-hidden="true"></i>
                    {t('single_provider.experience')}
                  </span>
                  <span className={styles.infoValue}>{offerExperience}{t('single_provider.years')}</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>
                    <i className="fa fa-map-marker" aria-hidden="true"></i>
                    {t('single_provider.location')}
                  </span>
                  <span className={styles.infoValue}>{offerLocation}</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>
                    <i className="fa fa-calendar-check" aria-hidden="true"></i>
                    {t('single_provider.availability')}
                  </span>
                  <span className={styles.infoValue}>{t('single_provider.availability_immediate')}</span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className={styles.actionButtons}>
              {isProviderActive ? (
                <button onClick={showBookModal} className={styles.bookBtn} disabled={isVerifyingSession}>
                  {isVerifyingSession ? (
                    <>
                      <i className="fa fa-spinner fa-spin" aria-hidden="true"></i>
                      <span>{t('single_provider.verifying', 'Verifying...')}</span>
                    </>
                  ) : (
                    <>
                      <i className="fa fa-calendar-check" aria-hidden="true"></i>
                      <span>{t('single_provider.book_now')}</span>
                    </>
                  )}
                </button>
              ) : (
                <div className={styles.inactiveBookingWrap}>
                  <button className={styles.bookBtnDisabled} disabled>
                    {t('single_provider.currently_unavailable')}
                  </button>
                  <p className={styles.inactiveNote}>
                    {t('single_provider.unavailable_note')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {relatedOffers.length > 0 && (
          <div className={styles.relatedOffersSection}>
            <h2 className={styles.relatedOffersTitle}>
              <i className="fa fa-layer-group" aria-hidden="true"></i>
              {t('single_provider.related_offers')}
            </h2>
            <div className={styles.relatedOffersGrid}>
              {relatedOffers.map((relatedOffer) => (
                <ServicesComponent
                  key={relatedOffer._id}
                  serviceId={relatedOffer._id}
                  name={relatedOffer.title}
                  description={relatedOffer.description}
                  fee={relatedOffer.price}
                  priceUnit={relatedOffer.priceUnit}
                  category={relatedOffer.service?.name}
                  location={
                    relatedOffer.location ||
                    relatedOffer.userService?.location ||
                    t('services_page.location_not_specified')
                  }
                  images={relatedOffer.images}
                  availability={t('services_page.available')}
                  isProviderActive={relatedOffer.userService?.isActive ?? false}
                />
              ))}
            </div>
          </div>
        )}

        {loadingRelated && relatedOffers.length === 0 && (
          <div className={styles.relatedOffersSection}>
            <h2 className={styles.relatedOffersTitle}>
              <i className="fa fa-layer-group" aria-hidden="true"></i>
              {t('single_provider.related_offers')}
            </h2>
            <div className={styles.relatedLoading}>{t('single_provider.loading_related')}</div>
          </div>
        )}
      </div>
    </>
  );
};

export default SingleProvider;